import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Users, ChevronLeft, ChevronRight, IdCard, AlertCircle, Pencil, Trash2, PlayCircle, ChevronUp, Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useT, useLang } from '@/i18n';
import { cn } from '@/lib/utils';
import {
  householdApi,
  HOUSEHOLD_RELATIONSHIP_TYPES,
  type CreateHouseholdMemberInput,
  type HouseholdMemberView,
  type HouseholdStateRow,
  type HouseholdDeclineReason,
} from './household.api';
import { ApiError } from '@/lib/api-client';
import { EditHouseholdMemberModal } from './EditHouseholdMemberModal';
import { DeleteHouseholdMemberModal } from './DeleteHouseholdMemberModal';
import { HouseholdScreeningPanel } from './HouseholdScreeningPanel';
import { DeclineModal } from './DeclineModal';
import {
  deriveHouseholdScreeningState,
  isTerminalState,
  type HouseholdDerivedState,
} from './derive-household-state';

interface Props {
  screeningId: string;
  onContinue: () => void;
  onBack: () => void;
}

type Mode = 'asking' | 'capturing';

/** Tailwind class per derived state. Kept colocated with the component so
 *  the visual mapping is a single source of truth for HouseholdMembersStep. */
const HOUSEHOLD_STATE_BADGE_CLASS: Record<HouseholdDerivedState, string> = {
  // v0.6.1 Fase 2.5 — pending is now an INCOMPLETE state that blocks
  // Continuar (same as in_progress); the red tint signals that to the agent.
  // F5-R1a: tokens del brand — el rojo se queda porque pending/rejected SON
  // bloqueos reales del submit (regla «rojo solo alertas reales»).
  pending: 'bg-nav-redTint text-nav-red border-nav-red',
  in_progress: 'bg-nav-amberTint text-nav-amberText border-nav-amber',
  completed: 'bg-nav-tealTint text-nav-tealDark border-nav-teal',
  validated: 'bg-nav-sageTint text-nav-sageText border-nav-sage',
  declined: 'bg-nav-hair text-nav-slate border-nav-card',
  rejected_by_uploader: 'bg-nav-redTint text-nav-red border-nav-red',
};

/** Best-effort relationship parse from the notes field — drawer-era convention
 *  is to store 'relationship:<value>' as a prefix in member notes when known. */
function parseRelationship(notes: string | null): string | null {
  if (!notes) return null;
  const m = /relationship:([a-z_]+)/i.exec(notes);
  return m?.[1] ?? null;
}

export function HouseholdMembersStep({ screeningId, onContinue, onBack }: Props): JSX.Element {
  const t = useT();
  const lang = useLang();
  const qc = useQueryClient();

  const listQ = useQuery({
    queryKey: ['screening', screeningId, 'household-members'],
    queryFn: () => householdApi.listForScreening(screeningId),
    staleTime: 0,
  });

  // If members already captured (e.g. user came back), default to capturing mode.
  const initialMode: Mode = (listQ.data?.length ?? 0) > 0 ? 'capturing' : 'asking';
  const [mode, setMode] = useState<Mode>(initialMode);

  // Form state
  const [fullName, setFullName] = useState('');
  const [dob, setDob] = useState('');
  const [medicaidId, setMedicaidId] = useState('');
  // v0.6.1 Fase 4 — optional relationship to source client.
  // Empty string means "not selected" — converted to undefined before POST.
  const [relationshipType, setRelationshipType] = useState<string>('');
  const [formError, setFormError] = useState<string | null>(null);

  // Cluster C — edit / delete modal state. null = closed.
  const [editingMember, setEditingMember] = useState<HouseholdMemberView | null>(null);
  const [deletingMember, setDeletingMember] = useState<HouseholdMemberView | null>(null);

  // Cluster H v2 — inline panel expansion + DeclineModal mount.
  // expandedMemberId: hmId whose HouseholdScreeningPanel is currently mounted.
  // decliningMember: when non-null, DeclineModal is mounted for this hm.
  //   SUB-E (LD-H2-12/13): replaces the prior inline two-click confirm with
  //   a modal overlay that captures a structured reason (5 preset radios +
  //   conditional textarea for 'other').
  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);
  const [decliningMember, setDecliningMember] = useState<{ hmId: string; fullName: string } | null>(null);
  // v0.6.0 Issue 6 — declined members live in a collapsed section by default.
  // Audit trail visible to supervisor/admin via toggle. Toggle is screening-
  // scoped local state; not persisted across remounts (intentional — declined
  // section should re-collapse when agent returns).
  const [showDeclined, setShowDeclined] = useState(false);

  // Cluster H v2 — per-member derived state (6 visual states) from the
  // /screenings/:id/household-states endpoint. Pure derivation in the UI
  // means new state transitions (e.g. add 'paused') do NOT need a backend
  // redeploy — just update derive-household-state.ts.
  const statesQ = useQuery<HouseholdStateRow[]>({
    queryKey: ['screening', screeningId, 'household-states'],
    queryFn: () => householdApi.listHouseholdStates(screeningId),
    staleTime: 5_000,
    refetchOnWindowFocus: true,
  });
  const stateByHmId = useMemo(() => {
    const m = new Map<string, HouseholdStateRow>();
    for (const r of statesQ.data ?? []) m.set(r.hmId, r);
    return m;
  }, [statesQ.data]);

  // Cluster H v2 SUB-E — decline mutation now takes structured body
  // (LD-H2-13). DeclineModal owns the form + calls handleConfirmDecline
  // which awaits the mutation; modal closes on success via its own logic
  // (this onSuccess just invalidates the query cache).
  const declineMut = useMutation({
    mutationFn: (vars: {
      hmId: string;
      body: { reason: HouseholdDeclineReason; reasonText?: string | null };
    }) => householdApi.decline(screeningId, vars.hmId, vars.body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['screening', screeningId, 'household-states'] });
      void qc.invalidateQueries({ queryKey: ['screening', screeningId, 'household-members'] });
    },
  });

  const handleConfirmDecline = async (
    body: { reason: HouseholdDeclineReason; reasonText?: string | null },
  ): Promise<void> => {
    if (!decliningMember) return;
    await declineMut.mutateAsync({ hmId: decliningMember.hmId, body });
  };

  const captureMut = useMutation({
    mutationFn: (input: CreateHouseholdMemberInput) =>
      householdApi.capture(screeningId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['screening', screeningId, 'household-members'] });
      setFullName('');
      setDob('');
      setMedicaidId('');
      setRelationshipType('');
      setFormError(null);
    },
  });

  const members: HouseholdMemberView[] = listQ.data ?? [];

  // v0.6.0 Issues 5+6 — derive aggregates from per-member derived states in a
  // single pass. declinedCount feeds Issue 6 toggle. pendingMembers (state
  // 'in_progress' — sub-screening exists but is_submitted=false) drives the
  // Issue 5 advancement guard: parent "Siguiente" hard-blocks while any HM
  // is in this state so agents don't accidentally abandon mid-flow sub-
  // screenings (Agent F handoff #5 deferred this).
  // v0.6.1 Fase 2.5 — extend incomplete set to BOTH 'pending' and
  // 'in_progress' derived states. Pending = HM captured but no sub-screening
  // started yet. In_progress = sub started, not submitted. Both block the
  // parent submit (the backend pre-check throws INCOMPLETE_HOUSEHOLD_MEMBERS
  // for either) so the UI must mirror that — agents can't accidentally
  // advance and then be rejected at submit time.
  const { declinedCount, pendingMembers, incompleteMembers } = useMemo(() => {
    let declined = 0;
    const inProgress: Array<{ hmId: string; fullName: string }> = [];
    const incomplete: Array<{ hmId: string; fullName: string; state: 'pending' | 'in_progress' }> = [];
    for (const m of members) {
      const stateRow = stateByHmId.get(m.id);
      const derived: HouseholdDerivedState = stateRow
        ? deriveHouseholdScreeningState(stateRow)
        : 'pending';
      if (derived === 'declined') declined++;
      if (derived === 'in_progress') {
        inProgress.push({ hmId: m.id, fullName: m.fullName });
        incomplete.push({ hmId: m.id, fullName: m.fullName, state: 'in_progress' });
      }
      if (derived === 'pending') {
        incomplete.push({ hmId: m.id, fullName: m.fullName, state: 'pending' });
      }
    }
    return {
      declinedCount: declined,
      pendingMembers: inProgress, // kept for the existing in_progress-only banner CTA
      incompleteMembers: incomplete,
    };
  }, [members, stateByHmId]);
  const canAdvance = incompleteMembers.length === 0;

  const handleAdd = (): void => {
    setFormError(null);
    const name = fullName.trim();
    if (name.length < 2) {
      setFormError(t('screening.household.errorRequired'));
      return;
    }
    if (!dob) {
      setFormError(t('screening.household.errorRequired'));
      return;
    }
    const d = new Date(dob);
    if (Number.isNaN(d.getTime()) || d > new Date() || d.getFullYear() < 1900) {
      setFormError(t('screening.household.errorInvalidDate'));
      return;
    }
    // Duplicate check (same name + dob)
    const dupe = members.find(
      (m) => m.fullName.trim().toLowerCase() === name.toLowerCase() && m.dob === dob,
    );
    if (dupe) {
      setFormError(t('screening.household.errorAlreadyAdded'));
      return;
    }
    captureMut.mutate({
      fullName: name,
      dob,
      medicaidId: medicaidId.trim() ? medicaidId.trim() : undefined,
      relationshipType:
        relationshipType !== ''
          ? (relationshipType as CreateHouseholdMemberInput['relationshipType'])
          : undefined,
    });
  };

  if (mode === 'asking' && members.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-nav-tealTint text-nav-tealDark flex items-center justify-center">
            <Users className="h-5 w-5" aria-hidden="true" />
          </div>
          <div>
            <h2 className="font-head text-lg font-bold text-nav-navy tracking-tight">
              {t('screening.household.prompt')}
            </h2>
            <p className="text-sm text-nav-slate mt-0.5">
              {t('screening.household.subtitle')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setMode('capturing')}
            className={cn(
              'rounded-xl border-[1.5px] border-nav-teal bg-nav-tealTint hover:border-nav-tealDark',
              'px-5 py-6 text-left transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
            )}
          >
            <div className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-nav-tealDark" />
              <span className="font-semibold text-nav-navy">{t('screening.household.yes')}</span>
            </div>
            <p className="text-xs text-nav-slate mt-2">{t('screening.household.yesHint')}</p>
          </button>
          <button
            type="button"
            onClick={onContinue}
            className={cn(
              'rounded-xl border-[1.5px] border-nav-amber bg-nav-amberTint hover:border-nav-amberText',
              'px-5 py-6 text-left transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
            )}
          >
            <div className="flex items-center gap-2">
              <ChevronRight className="h-5 w-5 text-nav-amberText" />
              <span className="font-semibold text-nav-navy">{t('screening.household.no')}</span>
            </div>
            <p className="text-xs text-nav-slate mt-2">{t('screening.household.noHint')}</p>
          </button>
        </div>

        <div className="flex items-center justify-between gap-3 pt-4 mt-4 border-t border-nav-hair">
          <Button variant="ghost" onClick={onBack}>
            <ChevronLeft className="h-4 w-4" />
            {t('screening.household.back')}
          </Button>
          <p className="font-head text-[11px] uppercase tracking-wide text-nav-slate font-bold">
            {t('screening.household.stepLabel')}
          </p>
          <span />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-nav-tealTint text-nav-tealDark flex items-center justify-center">
          <Users className="h-5 w-5" aria-hidden="true" />
        </div>
        <div>
          <h2 className="font-head text-lg font-bold text-nav-navy tracking-tight">
            {t('screening.household.capturedTitle')}
          </h2>
          <p className="text-sm text-nav-slate mt-0.5">
            {t('screening.household.capturedCount', { n: members.length })}
          </p>
        </div>
      </div>

      {/* v0.6.0 Issue 5 — advancement guard banner. Shown only when there are
          in_progress sub-screenings (agent started but didn't submit). CTA
          scroll-to-first-pending so agent finishes the work instead of
          accidentally advancing past HouseholdMembersStep. */}
      {pendingMembers.length > 0 && (
        <Alert variant="warning" role="alert">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{t('householdStep.guard.banner.title')}</AlertTitle>
          <AlertDescription>
            {t('householdStep.guard.banner.body', { count: pendingMembers.length })}
            <button
              type="button"
              onClick={() => {
                const first = pendingMembers[0];
                if (!first) return;
                setExpandedMemberId(first.hmId);
                document.getElementById(`hh-row-${first.hmId}`)?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                });
              }}
              className="ml-2 underline font-semibold text-nav-amberText hover:text-nav-amber"
            >
              {t('householdStep.guard.banner.cta')}
            </button>
          </AlertDescription>
        </Alert>
      )}

      {/* List of captured members */}
      {listQ.isLoading ? (
        <div className="py-8 text-center">
          <Loader2 className="h-5 w-5 text-nav-teal animate-spin mx-auto" />
        </div>
      ) : members.length > 0 ? (
        <ul className="space-y-2" data-testid="household-list">
          {members.map((m) => {
            // Cluster H v2 — derive the visual state from the household-states
            // endpoint via deriveHouseholdScreeningState(). 6 states; defaults
            // to 'pending' when the endpoint hasn't loaded yet OR the row is
            // missing (e.g. between capture and first poll).
            const stateRow = stateByHmId.get(m.id);
            const derivedState: HouseholdDerivedState = stateRow
              ? deriveHouseholdScreeningState(stateRow)
              : 'pending';
            // v0.6.0 Issue 6 — hide declined rows from default view; user
            // expands via toggle below to audit-review. Keeps active list
            // visually clean while preserving HIPAA audit trail visibility.
            if (derivedState === 'declined' && !showDeclined) return null;
            const terminal = isTerminalState(derivedState);
            const isExpanded = expandedMemberId === m.id;
            const badgeClass = HOUSEHOLD_STATE_BADGE_CLASS[derivedState];
            const badgeLabel = t(`householdStep.statusBadge.${derivedState}` as never);
            const relationship = parseRelationship(m.notes);
            return (
              <li
                key={m.id}
                id={`hh-row-${m.id}`}
                className={cn(
                  'flex flex-col gap-0 rounded-[10px] border border-nav-card bg-white',
                  // v0.6.0 Issue 5 — in_progress (sub-screening unsubmitted)
                  // gets a strong left edge warning so the row is impossible
                  // to miss in a long list.
                  derivedState === 'in_progress' &&
                    'border-l-4 border-l-nav-amber ring-1 ring-nav-amber/40',
                  // v0.6.1 Fase 2.5 — pending (captured, no sub-screening yet)
                  // also blocks advancement; rose tint distinguishes from
                  // in_progress amber so agent sees the distinct action needed
                  // (start sub-screening OR decline).
                  derivedState === 'pending' &&
                    'border-nav-red ring-1 ring-nav-red/30',
                )}
              >
                <div className="flex flex-col gap-2 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-nav-navy truncate">{m.fullName}</p>
                    <p className="text-xs text-nav-slate">
                      {lang === 'es' ? 'Año de nac.:' : 'DOB year:'} {m.dobYear || '—'}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span
                      className={cn(
                        'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border',
                        badgeClass,
                      )}
                    >
                      {badgeLabel}
                    </span>
                    {m.medicaidId ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-nav-sageTint text-nav-sageText border border-nav-sage">
                        <IdCard className="h-3 w-3" />
                        {lang === 'es' ? 'Con Medicaid' : 'Medicaid'}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-nav-hair text-nav-slate border border-nav-card">
                        {lang === 'es' ? 'Sin Medicaid ID' : 'No Medicaid ID'}
                      </span>
                    )}

                    {/* SUB-E (LD-H2-12): decline button opens DeclineModal overlay
                        (replaces prior inline two-click confirm). The browser
                        modal pattern doesn't interrupt the call any more than
                        any other in-app modal does, and it captures structured
                        reason metadata required by LD-H2-13. */}
                    <>
                        {!terminal && (
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => {
                              if (isExpanded) {
                                setExpandedMemberId(null);
                              } else {
                                setExpandedMemberId(m.id);
                              }
                            }}
                            type="button"
                            className="h-9 px-4 text-sm"
                            title={t('householdStep.actions.screen')}
                          >
                            {isExpanded ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <PlayCircle className="h-4 w-4" />
                            )}
                            <span>
                              {isExpanded
                                ? t('householdStep.actions.collapseScreen')
                                : t('householdStep.actions.screen')}
                            </span>
                          </Button>
                        )}
                        {!terminal && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setDecliningMember({ hmId: m.id, fullName: m.fullName });
                              setExpandedMemberId(null);
                            }}
                            type="button"
                            className="h-7 px-2 text-xs text-nav-red hover:bg-nav-redTint"
                            title={t('householdStep.actions.decline')}
                          >
                            <Ban className="h-3.5 w-3.5" />
                            <span className="hidden sm:inline">
                              {t('householdStep.actions.decline')}
                            </span>
                          </Button>
                        )}
                        <button
                          type="button"
                          onClick={() => setEditingMember(m)}
                          className="p-1 rounded hover:bg-nav-soft text-nav-slate"
                          aria-label={t('householdStep.actions.edit' as never)}
                          title={t('householdStep.actions.edit' as never)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeletingMember(m)}
                          className="p-1 rounded hover:bg-nav-redTint text-nav-red"
                          aria-label={t('householdStep.actions.delete' as never)}
                          title={t('householdStep.actions.delete' as never)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                  </div>
                </div>

                {/* Cluster H v2 — inline expandable HouseholdScreeningPanel.
                    animate-slide-down (200ms ease-out, defined in tailwind.config.ts)
                    runs once when the wrapper mounts. On collapse the wrapper
                    unmounts instantly — exit animation intentionally skipped
                    per Fase 2.6 spec (CSS-only, no library). */}
                {isExpanded && (
                  <div className="px-3 pb-3 animate-slide-down">
                    <HouseholdScreeningPanel
                      parentScreeningId={screeningId}
                      hmId={m.id}
                      fullName={m.fullName}
                      dobYear={m.dobYear}
                      existingSubScreeningId={stateRow?.subScreeningId ?? null}
                      relationship={relationship}
                      derivedState={derivedState}
                      onClose={() => setExpandedMemberId(null)}
                      onSubmitted={() => setExpandedMemberId(null)}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}

      {/* v0.6.0 Issue 6 — declined-members toggle. Hidden when zero declined.
          Audit trail preserved (rows still rendered when expanded) per
          HIPAA-friendly Q-E lock. Toggle is local state, re-collapses on
          remount (intentional). */}
      {declinedCount > 0 && (
        <button
          type="button"
          onClick={() => setShowDeclined((v) => !v)}
          className={cn(
            'text-xs font-medium text-nav-slate hover:text-nav-navy',
            'inline-flex items-center gap-1.5 px-2 py-1 rounded-md',
            'hover:bg-nav-soft transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
          )}
        >
          {showDeclined ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
          {showDeclined
            ? t('householdStep.declined.section.toggleHide' as never)
            : t('householdStep.declined.section.toggleShow' as never, { count: declinedCount })}
        </button>
      )}

      {/* Add form */}
      <div className="rounded-xl border-[1.5px] border-nav-card bg-nav-softer p-4 space-y-3">
        <p className="font-head text-sm font-semibold text-nav-navy">{t('screening.household.addMember')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="hh-name">{t('screening.household.fullNameLabel')} *</Label>
            <Input
              id="hh-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder={t('screening.household.fullNamePlaceholder')}
              autoComplete="off"
            />
          </div>
          <div>
            <Label htmlFor="hh-dob">{t('screening.household.dobLabel')} *</Label>
            <Input
              id="hh-dob"
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              max={new Date().toISOString().slice(0, 10)}
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="hh-medicaid">
              {t('screening.household.medicaidIdLabel')}{' '}
              <span className="text-nav-faint font-normal">({t('screening.household.medicaidIdPlaceholder')})</span>
            </Label>
            <Input
              id="hh-medicaid"
              value={medicaidId}
              onChange={(e) => setMedicaidId(e.target.value)}
              placeholder={t('screening.household.medicaidIdPlaceholder')}
              autoComplete="off"
            />
          </div>
          {/* v0.6.1 Fase 4 — relationship to source client (optional). */}
          <div className="sm:col-span-2">
            <Label htmlFor="hh-relationship">
              {t('screening.household.relationshipLabel')}{' '}
              <span className="text-nav-faint font-normal">({t('screening.household.relationshipOptional')})</span>
            </Label>
            <select
              id="hh-relationship"
              value={relationshipType}
              onChange={(e) => setRelationshipType(e.target.value)}
              className="flex h-10 w-full rounded-lg border-[1.5px] border-nav-line bg-white px-3 py-2 text-sm text-nav-navy focus:outline-none focus:ring-2 focus:ring-nav-teal disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">{t('screening.household.relationshipPlaceholder')}</option>
              {HOUSEHOLD_RELATIONSHIP_TYPES.map((r) => (
                <option key={r} value={r}>
                  {t(`screening.household.relationship.${r}`)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {formError && (
          <Alert variant="warning" role="alert">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}
        {captureMut.error && (
          <Alert variant="error" role="alert">
            <AlertTitle>{lang === 'es' ? 'No se pudo guardar' : "Couldn't save"}</AlertTitle>
            <AlertDescription>
              {captureMut.error instanceof ApiError
                ? captureMut.error.message
                : (captureMut.error as Error).message}
            </AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end">
          <Button onClick={handleAdd} disabled={captureMut.isPending}>
            {captureMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            <Plus className="h-4 w-4" />
            {members.length === 0
              ? t('screening.household.addMember')
              : t('screening.household.addAnother')}
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-2 pt-4 mt-4 border-t border-nav-hair">
        <div className="flex items-center justify-between gap-3">
          <Button variant="ghost" onClick={onBack}>
            <ChevronLeft className="h-4 w-4" />
            {t('screening.household.back')}
          </Button>
          <p className="font-head text-[11px] uppercase tracking-wide text-nav-slate font-bold hidden sm:block">
            {t('screening.household.stepLabel')}
          </p>
          <Button
            onClick={onContinue}
            disabled={!canAdvance}
            title={!canAdvance ? t('householdStep.guard.continueBlocked') : undefined}
          >
            {/* v0.6.1 Fase 2.5 — when disabled, button label communicates
                the blocker count directly so agents don't have to scroll
                for context. Enabled state keeps the original label. */}
            {canAdvance
              ? t('screening.household.continue')
              : t('householdStep.continueBlockedCount', { count: incompleteMembers.length })}
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        {/* v0.6.1 Fase 2.5 — helper text under the button when blocked.
            Right-aligned to live below the Continuar button on desktop;
            stacks naturally on mobile (flex-col container above). */}
        {!canAdvance && (
          <p className="text-xs text-nav-red sm:text-right">
            {t('householdStep.continueBlockedHelper')}
          </p>
        )}
      </div>

      {/* Cluster C — Edit / Delete modals (rendered conditionally) */}
      {editingMember && (
        <EditHouseholdMemberModal
          member={editingMember}
          screeningId={screeningId}
          onClose={() => setEditingMember(null)}
        />
      )}
      {deletingMember && (
        <DeleteHouseholdMemberModal
          member={deletingMember}
          screeningId={screeningId}
          onClose={() => setDeletingMember(null)}
        />
      )}

      {/* Cluster H v2 SUB-E — DeclineModal overlay (LD-H2-12/13). */}
      <DeclineModal
        member={decliningMember}
        onClose={() => setDecliningMember(null)}
        onConfirm={handleConfirmDecline}
      />
    </div>
  );
}